import React, { FunctionComponent, useRef } from 'react';
import type MuxPlayerElement from '@mux/mux-player';
import { AnalysisResultsForItem, UploadRequestItem } from '@companion-professional/dctypes';
import { Download, Loader, VideoOff } from 'lucide-react';
import { VideoTypeContainer } from '../../components/VideoTypeContainer';
import { ItemAnalysis } from './ItemAnalysis';
import { Button, Card, ErrorAlert, Text } from '@companion-professional/components';
import { useDownloadVideo } from '../../hooks/useDownloadVideo';

interface RequestedItemProps {
  requestedItem?: UploadRequestItem;
  mlForItem?: AnalysisResultsForItem;
}

// RequestedItem is a component that displays the requested item details and its associated machine learning analysis
// results (if available and if enabled for the clinic).
export const RequestedItem: FunctionComponent<RequestedItemProps> = ({ requestedItem, mlForItem = {} }) => {
  const videoPlayerRef = useRef<MuxPlayerElement>(null);
  const { isDownloading, downloadVideo } = useDownloadVideo();

  if (!requestedItem) {
    return <ErrorAlert title="Error Loading Item" message="No requested item found." className="my-8" />;
  }

  // Video requested item
  if (requestedItem.item_type === 'video') {
    console.log(mlForItem);

    if (requestedItem.upload_count === 0) {
      return (
        <Card
          variant="muted"
          className="my-8 aspect-video w-full flex-col items-center justify-center gap-4 p-4 text-destructive"
        >
          <VideoOff size="36" />
          <Text as="div" weight="strong">
            Requested video not uploaded
          </Text>
        </Card>
      );
    }

    return (
      <>
        <VideoTypeContainer videoItemId={requestedItem.item_id} ref={videoPlayerRef} />
        <div className="mt-2 flex justify-end">
          <Button
            variant="outline"
            size="small"
            onClick={() => downloadVideo(requestedItem.item_id)}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Loader size={16} className="mr-2 animate-spin" />
            ) : (
              <Download size={16} className="mr-2" />
            )}
            Download
          </Button>
        </div>
        {mlForItem?.status === 'processing' ? (
          <Card variant="muted" className="flex-row items-center justify-start gap-2 p-4">
            <Loader width={16} className="animate-pulse" />
            Running Analysis ...
          </Card>
        ) : (
          Object.entries(mlForItem.results || {}).map(([key, itemMlResult]) => (
            <ItemAnalysis
              key={`ml-result-${requestedItem.item_id}-${key}`}
              itemMlResult={itemMlResult}
              requestedItem={requestedItem}
              goToTime={(time: number) => {
                if (videoPlayerRef?.current) {
                  videoPlayerRef.current.currentTime = time;
                }
              }}
            />
          ))
        )}
      </>
    );
  }

  // Other item types can be handled here (i.e. images, documents, etc.)

  return <Text tone="muted">Unsupported item type: {requestedItem.item_type}</Text>;
};
